/**
 * Perfil · contraseña, apariencia y operadores (spec 041, 043, 051).
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link } from 'react-router'

import { CLAVE_SESION, useSesion } from '../auth'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { api, ErrorApi } from '../lib/api'
import {
    CATALOGO_PLANTILLAS,
    esTema,
    PLANTILLA_DEFECTO,
    TEMA_DEFECTO,
    TEMAS,
    type Tema,
} from '../lib/apariencia'
import { filaVacia, normalizarFilas, type FilaOperador } from '../lib/operadores'
import { LONGITUD_MINIMA_PASSWORD, passwordValida } from '../lib/password-policy'

const ETIQUETA_TEMA: Record<Tema, string> = {
    auto: 'Auto (el del sistema)',
    dark: 'Oscuro',
    light: 'Claro',
}

export function Perfil() {
    const queryClient = useQueryClient()
    const { data: sesion } = useSesion()
    const esAdmin = sesion?.role === 'admin'
    const [guardandoApariencia, setGuardandoApariencia] = useState(false)
    const [avisoApariencia, setAvisoApariencia] = useState<string | null>(null)
    const [actual, setActual] = useState('')
    const [nueva, setNueva] = useState('')
    const [repite, setRepite] = useState('')
    const [aviso, setAviso] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [enviando, setEnviando] = useState(false)
    const [filasOperadores, setFilasOperadores] = useState<FilaOperador[]>([filaVacia()])
    const [avisoOperadores, setAvisoOperadores] = useState<string | null>(null)
    const [errorOperadores, setErrorOperadores] = useState<string | null>(null)
    const [guardandoOperadores, setGuardandoOperadores] = useState(false)

    const operadoresQ = useQuery({
        queryKey: ['operadores'],
        queryFn: () => api.get<{ operadores: FilaOperador[] }>('/auth/operadores'),
        enabled: esAdmin === true,
        retry: false,
    })

    useEffect(() => {
        if (operadoresQ.data) {
            const filas = operadoresQ.data.operadores
            setFilasOperadores(filas.length > 0 ? filas : [filaVacia()])
        }
    }, [operadoresQ.data])

    async function guardarApariencia(tema?: string, plantilla?: string) {
        setAvisoApariencia(null)
        setGuardandoApariencia(true)
        try {
            const r = await api.patch<{ tema: Tema; plantilla: string }>('/auth/apariencia', {
                tema: esTema(tema) ? tema : (sesion?.apariencia?.tema ?? TEMA_DEFECTO),
                plantilla: plantilla ?? sesion?.apariencia?.plantilla ?? PLANTILLA_DEFECTO,
            })
            queryClient.setQueryData(CLAVE_SESION, (prev: typeof sesion) =>
                prev ? { ...prev, apariencia: r } : prev,
            )
            setAvisoApariencia('Apariencia guardada.')
        } catch (err) {
            setAvisoApariencia(err instanceof ErrorApi ? err.message : 'No se pudo guardar')
        } finally {
            setGuardandoApariencia(false)
        }
    }

    async function guardar(e: React.FormEvent) {
        e.preventDefault()
        setError(null)
        setAviso(null)
        if (!passwordValida(nueva)) {
            setError(`La nueva debe tener al menos ${LONGITUD_MINIMA_PASSWORD} caracteres`)
            return
        }
        if (nueva !== repite) {
            setError('La nueva y la repetición no coinciden')
            return
        }
        setEnviando(true)
        try {
            await api.post('/auth/password', { actual, nueva })
            setActual('')
            setNueva('')
            setRepite('')
            setAviso('Contraseña cambiada.')
        } catch (err) {
            setError(err instanceof ErrorApi ? err.message : 'No se pudo cambiar')
        } finally {
            setEnviando(false)
        }
    }

    async function guardarOperadores(e: React.FormEvent) {
        e.preventDefault()
        setErrorOperadores(null)
        setAvisoOperadores(null)
        const n = normalizarFilas(filasOperadores)
        if (!n.ok) {
            setErrorOperadores(n.error)
            return
        }
        setGuardandoOperadores(true)
        try {
            const r = await api.put<{ operadores: FilaOperador[] }>('/auth/operadores', {
                operadores: n.operadores,
            })
            setFilasOperadores(r.operadores.length > 0 ? r.operadores : [filaVacia()])
            queryClient.setQueryData(['operadores'], r)
            setAvisoOperadores(
                r.operadores.length === 0
                    ? 'Sin operadores. El dueño de cada negocio sigue en su ficha.'
                    : `Guardados ${r.operadores.length}. Son dueño en todos los negocios.`,
            )
        } catch (err) {
            setErrorOperadores(err instanceof ErrorApi ? err.message : 'No se pudo guardar')
        } finally {
            setGuardandoOperadores(false)
        }
    }

    return (
        <div className="mx-auto max-w-md space-y-6" data-testid="perfil">
            <header className="space-y-1">
                <h1 className="text-lg font-semibold text-neutral-100">Perfil</h1>
                <p className="text-sm text-neutral-500">
                    Correo, contraseña y apariencia. El correo no se cambia aquí.
                </p>
            </header>

            {!sesion?.soloLectura && (
            <section
                className="space-y-3 rounded-lg border border-neutral-800 bg-neutral-900 p-4"
                aria-label="Apariencia"
                data-testid="apariencia"
            >
                <h2 className="text-sm font-semibold text-neutral-200">Apariencia</h2>
                <fieldset className="space-y-2">
                    <legend className="text-xs text-neutral-400">Tema</legend>
                    {TEMAS.map((t) => (
                        <label key={t} className="flex items-center gap-2 text-sm text-neutral-200">
                            <input
                                type="radio"
                                name="tema"
                                value={t}
                                checked={(sesion?.apariencia?.tema ?? TEMA_DEFECTO) === t}
                                disabled={guardandoApariencia}
                                onChange={() => void guardarApariencia(t, sesion?.apariencia?.plantilla)}
                            />
                            {ETIQUETA_TEMA[t]}
                        </label>
                    ))}
                </fieldset>
                <label className="block text-xs text-neutral-400">
                    Plantilla visual
                    <select
                        aria-label="Plantilla visual"
                        className="mt-1 w-full rounded-md border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm text-neutral-100"
                        value={sesion?.apariencia?.plantilla ?? PLANTILLA_DEFECTO}
                        disabled={guardandoApariencia}
                        onChange={(e) =>
                            void guardarApariencia(sesion?.apariencia?.tema, e.target.value)
                        }
                    >
                        {CATALOGO_PLANTILLAS.map((p) => (
                            <option key={p.id} value={p.id}>
                                {p.nombre}
                            </option>
                        ))}
                    </select>
                </label>
                {avisoApariencia && (
                    <p role="status" className="text-xs text-emerald-600">
                        {avisoApariencia}
                    </p>
                )}
            </section>
            )}

            <section className="space-y-1 rounded-lg border border-neutral-800 bg-neutral-900 p-4">
                <h2 className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                    Correo
                </h2>
                <p className="text-sm text-neutral-100">{sesion?.email ?? '…'}</p>
            </section>

            {esAdmin && (
                <form
                    className="space-y-3 rounded-lg border border-neutral-800 bg-neutral-900 p-4"
                    onSubmit={(e) => void guardarOperadores(e)}
                    data-testid="operadores"
                    aria-label="WhatsApp de operación"
                >
                    <h2 className="text-sm font-semibold text-neutral-200">WhatsApp de operación</h2>
                    <p className="text-xs text-neutral-500">
                        Estos números son dueño en todos los negocios: ver el día, anular, cancelar.
                        No son clientes. El nombre es para vosotros. El dueño de cada negocio sigue
                        en su ficha.
                    </p>
                    <ul className="space-y-2">
                        {filasOperadores.map((fila, i) => (
                            <li key={i} className="flex gap-2">
                                <Input
                                    className="flex-1"
                                    aria-label={`Nombre del socio ${i + 1}`}
                                    placeholder="Nombre"
                                    value={fila.nombre}
                                    onChange={(e) => {
                                        const siguiente = [...filasOperadores]
                                        siguiente[i] = { ...fila, nombre: e.target.value }
                                        setFilasOperadores(siguiente)
                                    }}
                                />
                                <Input
                                    className="w-36 font-mono"
                                    aria-label={`Móvil del socio ${i + 1}`}
                                    inputMode="tel"
                                    placeholder="Móvil"
                                    value={fila.telefono}
                                    onChange={(e) => {
                                        const siguiente = [...filasOperadores]
                                        siguiente[i] = {
                                            ...fila,
                                            telefono: e.target.value.replace(/\D/g, '').slice(-9),
                                        }
                                        setFilasOperadores(siguiente)
                                    }}
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    aria-label={`Quitar socio ${i + 1}`}
                                    onClick={() => {
                                        const resto = filasOperadores.filter((_, j) => j !== i)
                                        setFilasOperadores(resto.length > 0 ? resto : [filaVacia()])
                                    }}
                                >
                                    Quitar
                                </Button>
                            </li>
                        ))}
                    </ul>
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setFilasOperadores([...filasOperadores, filaVacia()])}
                        >
                            Añadir socio
                        </Button>
                        <Button type="submit" disabled={guardandoOperadores}>
                            {guardandoOperadores ? 'Guardando…' : 'Guardar'}
                        </Button>
                    </div>
                    {errorOperadores && (
                        <p role="alert" className="text-xs text-rose-400">
                            {errorOperadores}
                        </p>
                    )}
                    {avisoOperadores && (
                        <p role="status" className="text-xs text-emerald-600">
                            {avisoOperadores}
                        </p>
                    )}
                </form>
            )}

            <form
                className="space-y-3 rounded-lg border border-neutral-800 bg-neutral-900 p-4"
                onSubmit={(e) => void guardar(e)}
                autoComplete="off"
            >
                <h2 className="text-sm font-semibold text-neutral-200">Cambiar contraseña</h2>
                <label className="block text-xs text-neutral-400">
                    Actual
                    <Input
                        className="mt-1"
                        aria-label="Contraseña actual"
                        type="password"
                        autoComplete="current-password"
                        value={actual}
                        onChange={(e) => setActual(e.target.value)}
                        required
                    />
                </label>
                <label className="block text-xs text-neutral-400">
                    Nueva (≥ {LONGITUD_MINIMA_PASSWORD})
                    <Input
                        className="mt-1"
                        aria-label="Nueva contraseña"
                        type="password"
                        autoComplete="new-password"
                        value={nueva}
                        onChange={(e) => setNueva(e.target.value)}
                        required
                    />
                </label>
                <label className="block text-xs text-neutral-400">
                    Repetir nueva
                    <Input
                        className="mt-1"
                        aria-label="Repetir nueva contraseña"
                        type="password"
                        autoComplete="new-password"
                        value={repite}
                        onChange={(e) => setRepite(e.target.value)}
                        required
                    />
                </label>
                {error && (
                    <p role="alert" className="text-xs text-rose-400">
                        {error}
                    </p>
                )}
                {aviso && (
                    <p role="status" className="text-xs text-emerald-600">
                        {aviso}
                    </p>
                )}
                <Button type="submit" disabled={enviando || !actual || !nueva || !repite}>
                    {enviando ? 'Guardando…' : 'Guardar contraseña'}
                </Button>
            </form>

            <Link to="/" className="inline-block text-xs text-neutral-500 hover:text-neutral-300">
                ← Volver
            </Link>
        </div>
    )
}
